import type { CSSProperties, ReactNode } from 'react';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 16 Oct 2026 · The light behind the Agent's composer.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The homescreen mockup's `h2Glow` (Kezdőlap) and `agGlow` (the Agent's empty state): two soft
 * ovals of the primary colour breathing out of phase behind the question box. Measured from the
 * mockup — 340 × 1240 and 220 × 680, blurred 36 and 40 px — and drawn in `--primary`, so a
 * workspace theme recolours it with everything else.
 *
 * ⚠️ **Decoration and nothing else**: `aria-hidden`, no pointer events, and still under
 * `prefers-reduced-motion` (`base.css`).
 *
 * `around` · the mockup draws it two ways. On Kezdőlap the light sits in a 600 px band across the
 * top of the page; on the Agent it is pinned to the composer's own centre, so it stays behind the
 * box wherever the empty state centres it.
 */

const OUTER: CSSProperties = {
  background:
    'radial-gradient(closest-side, color-mix(in oklch, var(--primary) 34%, transparent), color-mix(in oklch, var(--primary) 22%, transparent) 25%, color-mix(in oklch, var(--primary) 8%, transparent) 55%, color-mix(in oklch, var(--primary) 2%, transparent) 80%, transparent)',
  filter: 'blur(36px)',
};

const INNER: CSSProperties = {
  background:
    'radial-gradient(closest-side, color-mix(in oklch, var(--primary) 26%, transparent), color-mix(in oklch, var(--primary) 9%, transparent) 45%, color-mix(in oklch, var(--primary) 2%, transparent) 75%, transparent)',
  filter: 'blur(40px)',
};

export function ComposerGlow({ around }: { readonly around: 'page' | 'composer' }): ReactNode {
  if (around === 'page') {
    return (
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[600px] overflow-hidden"
      >
        <div
          className="composer-glow absolute top-[176px] left-1/2 h-[340px] w-[min(1240px,150%)] rounded-[50%]"
          style={OUTER}
        />
        <div
          className="composer-glow-2 absolute top-[184px] left-[54%] h-[220px] w-[min(680px,90%)] rounded-[50%]"
          style={INNER}
        />
      </div>
    );
  }
  return (
    <div aria-hidden className="pointer-events-none absolute top-1/2 left-1/2 -z-10 h-0 w-0">
      <div
        className="composer-glow absolute top-0 left-0 h-[340px] w-[1240px] rounded-[50%]"
        style={OUTER}
      />
      <div
        className="composer-glow-2 absolute top-[8px] left-[50px] h-[220px] w-[680px] rounded-[50%]"
        style={INNER}
      />
    </div>
  );
}
